import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Plug } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

type Truck = {
  id: string;
  unit_number: string;
  make: string | null;
  model: string | null;
  year: number | null;
  equipment_type: string;
  status: string;
  license_plate: string | null;
  samsara_vehicle_id: string | null;
  last_location_at: string | null;
};

type Driver = {
  id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  status: string;
  cdl_state: string | null;
  equipment_types: string[];
};

type Trailer = {
  id: string;
  unit_number: string;
  equipment_type: string;
  length_ft: number | null;
  capacity_lbs: number | null;
  status: string;
  current_location: string | null;
};

type SamsaraStatus = {
  connected: boolean;
  status: string;
  is_sandbox?: boolean;
  last_vehicles_sync_at: string | null;
  last_drivers_sync_at: string | null;
};

type MotiveStatus = {
  connected: boolean;
  status: string;
  is_sandbox?: boolean;
  last_vehicles_sync_at: string | null;
  last_drivers_sync_at: string | null;
};

const STATUS_BADGE: Record<string, string> = {
  available: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  assigned: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  out_of_service: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  maintenance: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  off_duty: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  inactive: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
};

function Badge({ label, status }: { label?: string; status: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[status] ?? "bg-muted text-muted-foreground"}`}>
      {label ?? status.replace("_", " ")}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function FleetPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState<"trucks" | "drivers" | "trailers">("trucks");
  const [trucks, setTrucks] = React.useState<Truck[]>([]);
  const [drivers, setDrivers] = React.useState<Driver[]>([]);
  const [trailers, setTrailers] = React.useState<Trailer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [samsara, setSamsara] = React.useState<SamsaraStatus | null>(null);
  const [motive, setMotive] = React.useState<MotiveStatus | null>(null);
  const [syncing, setSyncing] = React.useState(false);
  const [syncingMotive, setSyncingMotive] = React.useState(false);

  // Add form state
  const [showAddTruck, setShowAddTruck] = React.useState(false);
  const [showAddDriver, setShowAddDriver] = React.useState(false);
  const [showAddTrailer, setShowAddTrailer] = React.useState(false);
  const [addError, setAddError] = React.useState<string | null>(null);

  async function fetchAll() {
    setLoading(true);
    try {
      const [t, d, tr, s, m] = await Promise.all([
        fetch("/api/tms/fleet/trucks").then((r) => r.json()) as Promise<{ status: string; data?: Truck[] }>,
        fetch("/api/tms/fleet/drivers").then((r) => r.json()) as Promise<{ status: string; data?: Driver[] }>,
        fetch("/api/tms/fleet/trailers").then((r) => r.json()) as Promise<{ status: string; data?: Trailer[] }>,
        fetch("/api/tms/integrations/samsara/status").then((r) => r.json()) as Promise<{ status: string; data?: SamsaraStatus }>,
        fetch("/api/tms/integrations/motive/status").then((r) => r.json()) as Promise<{ status: string; data?: MotiveStatus }>,
      ]);
      if (t.status === "success" && t.data) setTrucks(t.data);
      if (d.status === "success" && d.data) setDrivers(d.data);
      if (tr.status === "success" && tr.data) setTrailers(tr.data);
      if (s.status === "success" && s.data) setSamsara(s.data);
      if (m.status === "success" && m.data) setMotive(m.data);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => { void fetchAll(); }, []);

  async function handleSamsaraSync() {
    setSyncing(true);
    await fetch("/api/tms/integrations/samsara/sync", { method: "POST" });
    await fetchAll();
    setSyncing(false);
  }

  async function handleMotiveSync() {
    setSyncingMotive(true);
    await fetch("/api/tms/integrations/motive/sync", { method: "POST" });
    await fetchAll();
    setSyncingMotive(false);
  }

  async function handleDeleteTruck(id: string) {
    await fetch(`/api/tms/fleet/trucks/${id}`, { method: "DELETE" });
    setTrucks((t) => t.filter((x) => x.id !== id));
  }

  async function handleDeleteDriver(id: string) {
    await fetch(`/api/tms/fleet/drivers/${id}`, { method: "DELETE" });
    setDrivers((d) => d.filter((x) => x.id !== id));
  }

  async function handleDeleteTrailer(id: string) {
    await fetch(`/api/tms/fleet/trailers/${id}`, { method: "DELETE" });
    setTrailers((t) => t.filter((x) => x.id !== id));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Fleet Registry</h1>
          <p className="text-sm text-muted-foreground">Manage trucks, drivers, and trailers</p>
        </div>
        <div className="flex items-center gap-3">
          {samsara && samsara.connected ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span>Samsara</span>
              {samsara.is_sandbox ? (
                <span className="rounded-full border border-amber-400/50 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                  Sandbox
                </span>
              ) : null}
              {samsara.last_vehicles_sync_at ? ` · synced ${formatDate(samsara.last_vehicles_sync_at)}` : ""}
              <Button size="sm" variant="outline" disabled={syncing} onClick={() => void handleSamsaraSync()}>
                {syncing ? "Syncing..." : "Sync Now"}
              </Button>
            </div>
          ) : null}
          {motive && motive.connected ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <span>Motive</span>
              {motive.is_sandbox ? (
                <span className="rounded-full border border-amber-400/50 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                  Sandbox
                </span>
              ) : null}
              {motive.last_vehicles_sync_at ? ` · synced ${formatDate(motive.last_vehicles_sync_at)}` : ""}
              <Button size="sm" variant="outline" disabled={syncingMotive} onClick={() => void handleMotiveSync()}>
                {syncingMotive ? "Syncing..." : "Sync Now"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Integration banner — shown when no telematics provider is connected */}
      {!loading && !samsara?.connected && !motive?.connected && trucks.length === 0 && drivers.length === 0 ? (
        <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Plug className="size-4 mt-0.5 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium">Connect a telematics provider</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Link Samsara or Motive to auto-sync your trucks and drivers. Sandbox access is free — no hardware needed to test.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/account/integrations")} className="shrink-0">
            Set up integrations
          </Button>
        </div>
      ) : null}

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {(["trucks", "drivers", "trailers"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
              activeTab === tab ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab} ({tab === "trucks" ? trucks.length : tab === "drivers" ? drivers.length : trailers.length})
          </button>
        ))}
      </div>

      {addError ? <p className="text-sm text-destructive">{addError}</p> : null}

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <>
          {/* TRUCKS */}
          {activeTab === "trucks" && (
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Trucks ({trucks.length})</CardTitle>
                <Button size="sm" onClick={() => { setAddError(null); setShowAddTruck(true); }}>+ Add Truck</Button>
              </CardHeader>
              <CardContent>
                {showAddTruck && (
                  <AddTruckForm
                    onCancel={() => setShowAddTruck(false)}
                    onSaved={(t) => { setTrucks((prev) => [t, ...prev]); setShowAddTruck(false); }}
                    onError={setAddError}
                  />
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">Unit</th>
                        <th className="pb-2 pr-4 font-medium">Type</th>
                        <th className="pb-2 pr-4 font-medium">Make/Model</th>
                        <th className="pb-2 pr-4 font-medium">Status</th>
                        <th className="pb-2 pr-4 font-medium">Last Location</th>
                        <th className="pb-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trucks.length === 0 ? (
                        <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No trucks added yet.</td></tr>
                      ) : trucks.map((t) => (
                        <tr key={t.id} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium">{t.unit_number}</td>
                          <td className="py-2 pr-4 capitalize text-muted-foreground">{t.equipment_type.replace("_", " ")}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{[t.year, t.make, t.model].filter(Boolean).join(" ") || "—"}</td>
                          <td className="py-2 pr-4"><Badge status={t.status} /></td>
                          <td className="py-2 pr-4 text-xs text-muted-foreground">{formatDate(t.last_location_at)}</td>
                          <td className="py-2">
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => void handleDeleteTruck(t.id)}>Remove</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* DRIVERS */}
          {activeTab === "drivers" && (
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Drivers ({drivers.length})</CardTitle>
                <Button size="sm" onClick={() => { setAddError(null); setShowAddDriver(true); }}>+ Add Driver</Button>
              </CardHeader>
              <CardContent>
                {showAddDriver && (
                  <AddDriverForm
                    onCancel={() => setShowAddDriver(false)}
                    onSaved={(d) => { setDrivers((prev) => [d, ...prev]); setShowAddDriver(false); }}
                    onError={setAddError}
                  />
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">Name</th>
                        <th className="pb-2 pr-4 font-medium">Phone</th>
                        <th className="pb-2 pr-4 font-medium">CDL</th>
                        <th className="pb-2 pr-4 font-medium">Status</th>
                        <th className="pb-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drivers.length === 0 ? (
                        <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No drivers added yet.</td></tr>
                      ) : drivers.map((d) => (
                        <tr key={d.id} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium">{d.first_name} {d.last_name}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{d.phone ?? "—"}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{d.cdl_state ?? "—"}</td>
                          <td className="py-2 pr-4"><Badge status={d.status} /></td>
                          <td className="py-2">
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => void handleDeleteDriver(d.id)}>Remove</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* TRAILERS */}
          {activeTab === "trailers" && (
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Trailers ({trailers.length})</CardTitle>
                <Button size="sm" onClick={() => { setAddError(null); setShowAddTrailer(true); }}>+ Add Trailer</Button>
              </CardHeader>
              <CardContent>
                {showAddTrailer && (
                  <AddTrailerForm
                    onCancel={() => setShowAddTrailer(false)}
                    onSaved={(t) => { setTrailers((prev) => [t, ...prev]); setShowAddTrailer(false); }}
                    onError={setAddError}
                  />
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">Unit</th>
                        <th className="pb-2 pr-4 font-medium">Type</th>
                        <th className="pb-2 pr-4 font-medium">Length</th>
                        <th className="pb-2 pr-4 font-medium">Status</th>
                        <th className="pb-2 pr-4 font-medium">Location</th>
                        <th className="pb-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trailers.length === 0 ? (
                        <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No trailers added yet.</td></tr>
                      ) : trailers.map((t) => (
                        <tr key={t.id} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium">{t.unit_number}</td>
                          <td className="py-2 pr-4 capitalize text-muted-foreground">{t.equipment_type.replace("_", " ")}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{t.length_ft ? `${t.length_ft} ft` : "—"}</td>
                          <td className="py-2 pr-4"><Badge status={t.status} /></td>
                          <td className="py-2 pr-4 text-muted-foreground">{t.current_location ?? "—"}</td>
                          <td className="py-2">
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => void handleDeleteTrailer(t.id)}>Remove</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// Mini add forms
function AddTruckForm({ onCancel, onSaved, onError }: { onCancel: () => void; onSaved: (t: Truck) => void; onError: (e: string) => void }) {
  const [f, setF] = React.useState({ unit_number: "", equipment_type: "van", make: "", model: "", year: "" });
  const [saving, setSaving] = React.useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    const res = await fetch("/api/tms/fleet/trucks", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...f, year: f.year ? Number(f.year) : undefined }) });
    const json = (await res.json()) as { status: string; data?: Truck; error?: string };
    if (json.status === "success" && json.data) { onSaved(json.data); } else { onError(json.error ?? "Failed to add truck."); }
    setSaving(false);
  }
  return (
    <form onSubmit={(e) => void submit(e)} className="mb-4 p-3 rounded-lg border bg-muted/30 grid grid-cols-2 gap-2 sm:grid-cols-4">
      <input required placeholder="Unit #" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.unit_number} onChange={(e) => setF((x) => ({ ...x, unit_number: e.target.value }))} />
      <select className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.equipment_type} onChange={(e) => setF((x) => ({ ...x, equipment_type: e.target.value }))}>
        {["van", "flatbed", "reefer", "step_deck", "box_truck"].map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      <input placeholder="Make" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.make} onChange={(e) => setF((x) => ({ ...x, make: e.target.value }))} />
      <input placeholder="Year" type="number" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.year} onChange={(e) => setF((x) => ({ ...x, year: e.target.value }))} />
      <div className="col-span-2 sm:col-span-4 flex gap-2">
        <Button size="sm" type="submit" disabled={saving}>{saving ? "Saving..." : "Add Truck"}</Button>
        <Button size="sm" type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

function AddDriverForm({ onCancel, onSaved, onError }: { onCancel: () => void; onSaved: (d: Driver) => void; onError: (e: string) => void }) {
  const [f, setF] = React.useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [saving, setSaving] = React.useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    const res = await fetch("/api/tms/fleet/drivers", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
    const json = (await res.json()) as { status: string; data?: Driver; error?: string };
    if (json.status === "success" && json.data) { onSaved(json.data); } else { onError(json.error ?? "Failed to add driver."); }
    setSaving(false);
  }
  return (
    <form onSubmit={(e) => void submit(e)} className="mb-4 p-3 rounded-lg border bg-muted/30 grid grid-cols-2 gap-2 sm:grid-cols-4">
      <input required placeholder="First Name" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.first_name} onChange={(e) => setF((x) => ({ ...x, first_name: e.target.value }))} />
      <input required placeholder="Last Name" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.last_name} onChange={(e) => setF((x) => ({ ...x, last_name: e.target.value }))} />
      <input placeholder="Phone" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.phone} onChange={(e) => setF((x) => ({ ...x, phone: e.target.value }))} />
      <input placeholder="Email" type="email" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.email} onChange={(e) => setF((x) => ({ ...x, email: e.target.value }))} />
      <div className="col-span-2 sm:col-span-4 flex gap-2">
        <Button size="sm" type="submit" disabled={saving}>{saving ? "Saving..." : "Add Driver"}</Button>
        <Button size="sm" type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

function AddTrailerForm({ onCancel, onSaved, onError }: { onCancel: () => void; onSaved: (t: Trailer) => void; onError: (e: string) => void }) {
  const [f, setF] = React.useState({ unit_number: "", equipment_type: "van", length_ft: "" });
  const [saving, setSaving] = React.useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    const res = await fetch("/api/tms/fleet/trailers", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...f, length_ft: f.length_ft ? Number(f.length_ft) : undefined }) });
    const json = (await res.json()) as { status: string; data?: Trailer; error?: string };
    if (json.status === "success" && json.data) { onSaved(json.data); } else { onError(json.error ?? "Failed to add trailer."); }
    setSaving(false);
  }
  return (
    <form onSubmit={(e) => void submit(e)} className="mb-4 p-3 rounded-lg border bg-muted/30 grid grid-cols-2 gap-2 sm:grid-cols-4">
      <input required placeholder="Unit #" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.unit_number} onChange={(e) => setF((x) => ({ ...x, unit_number: e.target.value }))} />
      <select className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.equipment_type} onChange={(e) => setF((x) => ({ ...x, equipment_type: e.target.value }))}>
        {["van", "flatbed", "reefer", "step_deck", "box_truck"].map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
      <input placeholder="Length (ft)" type="number" className="h-8 rounded border border-input bg-background px-2 text-sm" value={f.length_ft} onChange={(e) => setF((x) => ({ ...x, length_ft: e.target.value }))} />
      <div className="col-span-2 sm:col-span-4 flex gap-2">
        <Button size="sm" type="submit" disabled={saving}>{saving ? "Saving..." : "Add Trailer"}</Button>
        <Button size="sm" type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
